// Rik.ai — Aggregate Report PDF Component
// Uses @react-pdf/renderer for client-side PDF generation.
// Handles both the standard report schema AND any custom structure
// produced by admin instructions (two-layer rendering: known + generic).

import React from "react";
import {
  Document, Page, View, Text, StyleSheet, pdf, Font, Image,
  Svg, Defs, LinearGradient, Stop, Rect,
} from "@react-pdf/renderer";

// ── Fonts ──────────────────────────────────────────────────────────────────────
// Self-hosted static TTFs from each app's public/fonts/ (committed to the repo).
// Poppins = display/headings (brand font), Inter = body/data. No runtime CDN
// dependency. react-pdf needs static per-weight TTF/OTF (not woff2 / variable).
const FONT_BASE = `${process.env.PUBLIC_URL || ""}/fonts`;
Font.register({
  family: "Poppins",
  fonts: [
    { src: `${FONT_BASE}/Poppins_400Regular.ttf`,  fontWeight: 400 },
    { src: `${FONT_BASE}/Poppins_500Medium.ttf`,   fontWeight: 500 },
    { src: `${FONT_BASE}/Poppins_600SemiBold.ttf`, fontWeight: 600 },
    { src: `${FONT_BASE}/Poppins_700Bold.ttf`,     fontWeight: 700 },
  ],
});
Font.register({
  family: "Inter",
  fonts: [
    { src: `${FONT_BASE}/Inter_400Regular.ttf`,  fontWeight: 400 },
    { src: `${FONT_BASE}/Inter_500Medium.ttf`,   fontWeight: 500 },
    { src: `${FONT_BASE}/Inter_600SemiBold.ttf`, fontWeight: 600 },
    // Inter v4 (opsz axis) — the Regular/Medium/SemiBold files above are v3.019
    // (pre-opsz). 18pt is the lowest available optical size, designed for small
    // text — matches this file's actual use (8.5pt captions), not a v3 exact
    // match but visually indistinguishable at this size.
    { src: `${FONT_BASE}/Inter_18pt-Italic.ttf`,  fontWeight: 400, fontStyle: "italic" },
  ],
});
// Disable mid-word hyphenation for cleaner paragraph wrapping.
Font.registerHyphenationCallback((word) => [word]);

const DISPLAY = "Poppins";
const BODY    = "Inter";

// ── Colours (light theme — white background, dark text) ───────────────────────
const P = {
  page:      "#FFFFFF",
  panel:     "#F8FAFC",  // card / box fill
  hairline:  "#E5E7EB",
  ink:       "#111827",  // near-black body text
  secondary: "#475569",
  muted:     "#94A3B8",
  accent:    "#6D28D9",  // deep violet — headers, rules, figures
  accentSoft:"#EDE9FE",  // violet-50 chip fill
  blue:      "#2563EB",
  cyan:      "#06B6D4",
  gold:      "#D97706",
  positive:  "#059669",
  neutral:   "#CBD5E1",  // neutral bar fill (subdued)
  neutralTx: "#64748B",
  negative:  "#DC2626",
  white:     "#FFFFFF",
};

// Rik.ai logo (served from each app's public/ folder).
const LOGO_SRC = `${process.env.PUBLIC_URL || ""}/rik-logo.png`;

// ── Styles ────────────────────────────────────────────────────────────────────
const s = StyleSheet.create({
  page: {
    backgroundColor: P.page,
    color: P.ink,
    fontFamily: BODY,
    fontSize: 10.5,
    // Padding reserves space for the fixed header/footer on every auto-paginated page.
    paddingTop: 62,
    paddingBottom: 54,
    paddingHorizontal: 48,
  },
  section: { marginBottom: 6 },

  // ── Cover ──
  coverPage: { backgroundColor: P.page, position: "relative" },
  barTop:    { position: "absolute", top: 0, left: 0, width: 595, height: 6 },
  barBottom: { position: "absolute", bottom: 0, left: 0, width: 595, height: 6 },
  coverInner: { flex: 1, paddingHorizontal: 56, paddingTop: 70, paddingBottom: 60, justifyContent: "space-between" },
  coverBrandRow: { flexDirection: "row", alignItems: "center", gap: 12 },
  coverLogoImg: { width: 40, height: 40 },
  coverWordmark: { fontFamily: DISPLAY, fontWeight: 700, fontSize: 26, color: P.ink },
  coverTagline: { fontFamily: BODY, fontSize: 11, color: P.muted, marginTop: 8, letterSpacing: 0.3 },
  coverEyebrow: { fontFamily: DISPLAY, fontWeight: 600, fontSize: 11, color: P.accent, textTransform: "uppercase", letterSpacing: 1.6 },
  coverEyebrowRule: { width: 40, height: 3, backgroundColor: P.accent, borderRadius: 2, marginTop: 8, marginBottom: 18 },
  coverTitle: { fontFamily: DISPLAY, fontWeight: 600, fontSize: 34, color: P.ink, lineHeight: 1.2, marginBottom: 18 },
  coverMetaLine: { fontFamily: BODY, fontSize: 11, color: P.muted, marginBottom: 5 },
  coverMetaStrong: { fontFamily: BODY, fontWeight: 600, color: P.secondary },
  coverFooter: { flexDirection: "row", justifyContent: "space-between", alignItems: "flex-end" },
  coverFootText: { fontFamily: BODY, fontSize: 9, color: P.muted },

  // ── Content page (fixed header/footer positioned in the page padding) ──
  pageHeader: {
    position: "absolute", top: 22, left: 48, right: 48,
    flexDirection: "row", justifyContent: "space-between", alignItems: "center",
    paddingBottom: 8, borderBottomWidth: 1, borderBottomColor: P.hairline,
  },
  pageHeaderBrand: { fontFamily: DISPLAY, fontWeight: 600, fontSize: 10, color: P.ink },
  pageHeaderBrandSub: { fontFamily: BODY, fontWeight: 400, color: P.muted },
  pageHeaderTitle: { fontFamily: BODY, fontSize: 9, color: P.muted, maxWidth: 280, textAlign: "right" },
  pageFooter: { position: "absolute", bottom: 22, left: 48, right: 48, flexDirection: "row", justifyContent: "space-between", borderTopWidth: 1, borderTopColor: P.hairline, paddingTop: 6 },
  pageFooterText: { fontFamily: BODY, fontSize: 8.5, color: P.muted },

  // ── Section ──
  sectionTitle: { fontFamily: DISPLAY, fontWeight: 600, fontSize: 12.5, color: P.accent, textTransform: "uppercase", letterSpacing: 0.8, marginTop: 18 },
  sectionRule:  { width: 28, height: 2, backgroundColor: P.accent, borderRadius: 1, marginTop: 5, marginBottom: 12 },
  paragraph: { fontFamily: BODY, fontSize: 10.5, color: P.ink, lineHeight: 1.6, marginBottom: 8 },
  muted:     { fontFamily: BODY, fontSize: 9.5, color: P.secondary, lineHeight: 1.55, marginBottom: 6 },
  label:     { fontFamily: BODY, fontWeight: 500, fontSize: 8.5, color: P.muted, textTransform: "uppercase", letterSpacing: 0.6, marginTop: 8, marginBottom: 6 },

  // ── Cards ──
  card: { backgroundColor: P.panel, borderRadius: 8, padding: 12, marginBottom: 8, borderWidth: 1, borderColor: P.hairline },
  cardTitle: { fontFamily: DISPLAY, fontWeight: 600, fontSize: 10.5, color: P.ink, marginBottom: 4 },
  cardSubtext: { fontFamily: BODY, fontSize: 9.5, color: P.secondary, lineHeight: 1.5 },
  chip: { fontFamily: DISPLAY, fontWeight: 600, fontSize: 7.5, color: P.accent, backgroundColor: P.accentSoft, paddingVertical: 2, paddingHorizontal: 7, borderRadius: 9 },
  confChip: { fontFamily: DISPLAY, fontWeight: 600, fontSize: 7, textTransform: "uppercase", letterSpacing: 0.4, paddingVertical: 2, paddingHorizontal: 6, borderRadius: 9, borderWidth: 1 },
  metaTag: { fontFamily: DISPLAY, fontWeight: 500, fontSize: 7.5, color: P.muted, textTransform: "uppercase", letterSpacing: 0.4, marginRight: 10 },

  // Multi-column blocks (action summary, drivers/barriers)
  triCol:   { flexDirection: "row", gap: 8 },
  triPanel: { flex: 1, backgroundColor: P.panel, borderRadius: 8, padding: 10, borderWidth: 1, borderColor: P.hairline },
  triHead:  { fontFamily: DISPLAY, fontWeight: 600, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 },
  triItem:  { fontFamily: BODY, fontSize: 9, color: P.ink, lineHeight: 1.45, marginBottom: 3 },

  // ── Metrics ──
  metricsRow: { flexDirection: "row", gap: 8, marginBottom: 10 },
  metricBox: { flex: 1, backgroundColor: P.panel, borderRadius: 8, padding: 12, alignItems: "center", borderWidth: 1, borderColor: P.hairline },
  metricVal: { fontFamily: DISPLAY, fontWeight: 600, fontSize: 19, color: P.accent, marginBottom: 3 },
  metricLabel: { fontFamily: BODY, fontWeight: 500, fontSize: 7.5, color: P.muted, textTransform: "uppercase", letterSpacing: 0.5, textAlign: "center" },

  // ── Scorecard (Phase 3) ──
  scHero: { backgroundColor: P.accentSoft, borderRadius: 10, padding: 16, marginBottom: 12, borderWidth: 1, borderColor: P.hairline, flexDirection: "row", alignItems: "center", justifyContent: "space-between" },
  scHeroLeft: { flex: 1, paddingRight: 12 },
  scHeroLabel: { fontFamily: DISPLAY, fontWeight: 600, fontSize: 9, color: P.accent, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 4 },
  scHeroVal: { fontFamily: DISPLAY, fontWeight: 700, fontSize: 38, color: P.accent, lineHeight: 1 },
  scHeroInterpret: { fontFamily: BODY, fontSize: 9, color: P.secondary, marginTop: 6, lineHeight: 1.45 },
  scHeroNorm: { fontFamily: BODY, fontSize: 9, color: P.secondary, marginTop: 4 },
  scHeroNormUp: { color: "#15803D", fontWeight: 700 },
  scHeroNormDown: { color: "#B91C1C", fontWeight: 700 },
  scBand: { fontFamily: DISPLAY, fontWeight: 700, fontSize: 11, color: P.white, textTransform: "uppercase", letterSpacing: 0.6, paddingVertical: 5, paddingHorizontal: 12, borderRadius: 8, textAlign: "center" },
  segRow: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", paddingVertical: 4, borderBottomWidth: 1, borderBottomColor: P.hairline },
  segLabel: { fontFamily: BODY, fontSize: 9, color: P.ink, flex: 1 },
  segVal: { fontFamily: DISPLAY, fontWeight: 600, fontSize: 9.5, color: P.accent },
  segMuted: { fontFamily: BODY, fontSize: 8.5, color: P.muted, fontStyle: "italic" },
  funnelRow: { flexDirection: "row", alignItems: "center", gap: 8, marginBottom: 6 },
  funnelLabel: { fontFamily: BODY, fontSize: 9, color: P.ink, width: 120 },
  funnelBarBg: { flex: 1, height: 12, backgroundColor: P.hairline, borderRadius: 6, overflow: "hidden" },
  funnelBarFill: { height: 12, backgroundColor: P.accent, borderRadius: 6 },
  funnelPct: { fontFamily: DISPLAY, fontWeight: 600, fontSize: 9, color: P.accent, width: 56, textAlign: "right" },

  // ── Quote ──
  quoteBlock: { borderLeftWidth: 2.5, borderLeftColor: P.accent, paddingLeft: 11, marginBottom: 8, paddingVertical: 2 },
  quoteText: { fontFamily: BODY, fontSize: 10.5, color: P.ink, lineHeight: 1.5, marginBottom: 2 },
  quoteMeta: { fontFamily: BODY, fontSize: 8.5, color: P.muted },

  // ── Recommendations ──
  recRow: { flexDirection: "row", gap: 10, marginBottom: 8, padding: 12, backgroundColor: P.panel, borderRadius: 8, borderWidth: 1, borderColor: P.hairline },
  recLeft: { flex: 1 },
  recTitle: { fontFamily: DISPLAY, fontWeight: 600, fontSize: 10.5, color: P.ink, marginBottom: 3 },
  recDesc: { fontFamily: BODY, fontSize: 9.5, color: P.secondary, lineHeight: 1.5 },
  pill: { fontFamily: DISPLAY, fontWeight: 600, fontSize: 7, color: P.white, textTransform: "uppercase", letterSpacing: 0.5, paddingVertical: 2.5, paddingHorizontal: 7, borderRadius: 9, textAlign: "center" },
  pillOutline: { fontFamily: DISPLAY, fontWeight: 500, fontSize: 7, color: P.secondary, backgroundColor: P.white, borderWidth: 1, borderColor: P.hairline, textTransform: "uppercase", letterSpacing: 0.5, paddingVertical: 2, paddingHorizontal: 7, borderRadius: 9, textAlign: "center" },

  // ── Sentiment ──
  sentBar: { flexDirection: "row", height: 7, borderRadius: 4, overflow: "hidden", backgroundColor: P.hairline, marginVertical: 5 },
  sentLegendRow: { flexDirection: "row", gap: 14, marginTop: 2 },
  sentLegendItem: { flexDirection: "row", alignItems: "center", gap: 4 },
  sentDot: { width: 6, height: 6, borderRadius: 3 },
  sentLegendText: { fontFamily: BODY, fontSize: 8.5, color: P.secondary },

  // ── Generic ──
  genericCard: { backgroundColor: P.panel, borderRadius: 8, padding: 12, marginBottom: 8, borderWidth: 1, borderColor: P.hairline },
  genericKey: { fontFamily: BODY, fontWeight: 500, fontSize: 9, color: P.muted, marginBottom: 2, textTransform: "capitalize" },
  genericVal: { fontFamily: BODY, fontSize: 10.5, color: P.ink, lineHeight: 1.5 },
  divider: { height: 1, backgroundColor: P.hairline, marginVertical: 12 },
  bulletRow: { flexDirection: "row", gap: 6, marginBottom: 5 },
  bullet: { fontFamily: BODY, fontSize: 10.5, color: P.accent, lineHeight: 1.5 },
  bulletText: { flex: 1, fontFamily: BODY, fontSize: 10.5, color: P.ink, lineHeight: 1.5 },
});

// ── Known section keys (excluded from generic fallback) ───────────────────────
const KNOWN_KEYS = new Set([
  "executiveSummary", "actionSummary", "respondentProfile", "engagementMetrics", "keyThemes",
  "driversAndBarriers", "questionAnalysis", "verbatimHighlights", "sentimentAnalysis",
  "demographicInsights", "recommendations", "conclusion", "methodologyNote",
  "scorecard", "_stats",
]);

// ── Helpers ───────────────────────────────────────────────────────────────────
const camelToTitle = (key) =>
  key.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase()).trim();

const priorityColor = (p) =>
  p === "high" ? P.negative : p === "medium" ? P.gold : P.positive;

const sentimentColor = (s) =>
  s === "positive" ? P.positive : s === "negative" ? P.negative : P.neutralTx;

const confColor = (c) =>
  c === "high" ? P.positive : c === "low" ? P.negative : P.gold;

// Renders an action-summary / drivers-barriers column. cols = [{ head, color, items }]
function ColumnBlock({ cols }) {
  return (
    <View style={s.triCol}>
      {cols.map((c, i) => (
        <View key={i} style={s.triPanel}>
          <Text style={[s.triHead, { color: c.color }]}>{c.head}</Text>
          {(c.items || []).map((it, j) => (
            <View key={j} style={s.bulletRow}>
              <Text style={[s.bullet, { color: c.color }]}>•</Text>
              <Text style={s.triItem}>{it}</Text>
            </View>
          ))}
        </View>
      ))}
    </View>
  );
}

// Brand gradient bar (SVG). Unique id per instance to avoid def collisions.
function GradientBar({ style, id }) {
  return (
    <Svg style={style} width={595} height={6} viewBox="0 0 595 6">
      <Defs>
        <LinearGradient id={id} x1="0" y1="0" x2="595" y2="0" gradientUnits="userSpaceOnUse">
          <Stop offset="0" stopColor="#7C3AED" />
          <Stop offset="0.5" stopColor={P.blue} />
          <Stop offset="1" stopColor={P.cyan} />
        </LinearGradient>
      </Defs>
      <Rect x="0" y="0" width="595" height="6" fill={`url(#${id})`} />
    </Svg>
  );
}

// Stacked sentiment bar + legend.
function SentimentBar({ positive = 0, neutral = 0, negative = 0 }) {
  const total = (positive || 0) + (neutral || 0) + (negative || 0);
  if (!total) return null;
  return (
    <View>
      <View style={s.sentBar}>
        {positive > 0 && <View style={{ flex: positive, backgroundColor: P.positive }} />}
        {neutral  > 0 && <View style={{ flex: neutral,  backgroundColor: P.neutral }} />}
        {negative > 0 && <View style={{ flex: negative, backgroundColor: P.negative }} />}
      </View>
      <View style={s.sentLegendRow}>
        <View style={s.sentLegendItem}><View style={[s.sentDot, { backgroundColor: P.positive }]} /><Text style={s.sentLegendText}>Positive {positive}</Text></View>
        <View style={s.sentLegendItem}><View style={[s.sentDot, { backgroundColor: P.neutral }]} /><Text style={s.sentLegendText}>Neutral {neutral}</Text></View>
        <View style={s.sentLegendItem}><View style={[s.sentDot, { backgroundColor: P.negative }]} /><Text style={s.sentLegendText}>Negative {negative}</Text></View>
      </View>
    </View>
  );
}

// Section header + accent rule. Orphan protection is handled by the parent section
// being atomic (wrap={false}), so the header always travels with its content.
function SectionTitle({ children }) {
  return (
    <View>
      <Text style={s.sectionTitle}>{children}</Text>
      <View style={s.sectionRule} />
    </View>
  );
}

// Band colour for go/refine/kill or NPS quality bands.
const bandColor = (band) => {
  const b = String(band || "").toUpperCase();
  if (["GO", "EXCELLENT"].includes(b)) return P.positive;
  if (["REFINE", "GOOD"].includes(b)) return P.gold;
  if (["KILL", "NEEDS ATTENTION"].includes(b)) return P.negative;
  return P.accent;
};

// Append a metric's unit to its value (handles %, /5, nps, ₹, label units).
const fmtMetric = (m) => {
  if (m.value == null) return "—";
  const u = m.unit || "";
  if (u === "nps") return String(m.value);
  if (u === "%" || u.startsWith("% ")) return `${m.value}%`;
  if (u.startsWith("/")) return `${m.value}${u}`;
  if (u === "₹") return `₹${m.value}`;
  if (!u) return String(m.value);
  return `${m.value} ${u}`;
};

// ── Methodology Scorecard (Phase 3) ───────────────────────────────────────────
// Deterministic KPI block rendered as its own page right after the cover. Omitted
// entirely for qual_only studies (scorecard is null). Atomic sections (wrap=false)
// so KPI blocks never split across a page boundary.
function Scorecard({ scorecard, surveyTitle }) {
  if (!scorecard || !scorecard.headline) return null;
  const hd = scorecard.headline;
  const metrics = (scorecard.metrics || []).slice(0, 6);
  // "By segment" cuts are disabled — segment n counts silently exclude
  // supplier-sourced respondents (no demographic profile on file for them),
  // so the base shown would be misleading. See docs/product-backlog.md.
  const segments = [];
  const funnel = scorecard.funnel || null;
  const drivers = scorecard.drivers?.detraction || [];
  const maxPct = funnel ? Math.max(1, ...funnel.map((f) => f.pct || 0)) : 1;

  return (
    <Page size="A4" style={s.page}>
      <View style={s.pageHeader} fixed>
        <Text style={s.pageHeaderBrand}>Rik.ai<Text style={s.pageHeaderBrandSub}>  ·  Confidential</Text></Text>
        <Text style={s.pageHeaderTitle}>{surveyTitle}</Text>
      </View>

      <View wrap={false} style={s.section}>
        <SectionTitle>Methodology Scorecard</SectionTitle>

        {/* Headline KPI */}
        <View style={s.scHero}>
          <View style={s.scHeroLeft}>
            <Text style={s.scHeroLabel}>{hd.label}</Text>
            <Text style={s.scHeroVal}>{hd.value == null ? "—" : fmtMetric(hd)}</Text>
            {hd.interpretation ? <Text style={s.scHeroInterpret}>{hd.interpretation}</Text> : null}
            {hd.norm ? (
              <Text style={s.scHeroNorm}>
                vs benchmark {fmtMetric({ value: hd.norm.value, unit: hd.unit })}
                {hd.norm.delta != null ? (
                  <Text style={hd.norm.delta >= 0 ? s.scHeroNormUp : s.scHeroNormDown}>
                    {`  ${hd.norm.delta >= 0 ? "▲" : "▼"} ${hd.norm.delta >= 0 ? "+" : ""}${hd.norm.delta}`}
                  </Text>
                ) : null}
                <Text style={s.segMuted}>{`  (${hd.norm.sourceType})`}</Text>
              </Text>
            ) : null}
          </View>
          {hd.band ? <Text style={[s.scBand, { backgroundColor: bandColor(hd.band) }]}>{hd.band}</Text> : null}
        </View>

        {/* Secondary metrics */}
        {metrics.length > 0 && (
          <View style={[s.metricsRow, { flexWrap: "wrap" }]} wrap={false}>
            {metrics.map((m, i) => (
              <View key={i} style={[s.metricBox, { minWidth: 90 }]}>
                <Text style={s.metricVal}>{fmtMetric(m)}</Text>
                <Text style={s.metricLabel}>{m.label}</Text>
              </View>
            ))}
          </View>
        )}
      </View>

      {/* Funnel */}
      {funnel && funnel.length > 0 && (
        <View wrap={false} style={s.section}>
          <Text style={s.label}>Conversion Funnel</Text>
          {funnel.map((f, i) => (
            <View key={i} style={s.funnelRow}>
              <Text style={s.funnelLabel}>{f.label}</Text>
              <View style={s.funnelBarBg}>
                <View style={[s.funnelBarFill, { width: `${Math.round(((f.pct || 0) / maxPct) * 100)}%` }]} />
              </View>
              <Text style={s.funnelPct}>{f.pct == null ? "—" : `${f.pct}%`}</Text>
            </View>
          ))}
        </View>
      )}

      {/* By segment (small-cell suppression respected) */}
      {segments.length > 0 && (
        <View wrap={false} style={s.section}>
          <Text style={s.label}>{hd.label} by Segment</Text>
          {segments.map((sg, i) => (
            <View key={i} style={s.segRow}>
              <Text style={s.segLabel}>{sg.dimensionLabel}: {sg.segment} <Text style={s.segMuted}>(n={sg.base})</Text></Text>
              {sg.suppressed
                ? <Text style={s.segMuted}>insufficient base</Text>
                : <Text style={s.segVal}>{fmtMetric({ value: sg.value, unit: sg.unit })}</Text>}
            </View>
          ))}
        </View>
      )}

      {/* Strengths / pain points (product diagnostics) */}
      {(scorecard.strengths?.length || scorecard.painPoints?.length) ? (
        <View wrap={false} style={s.section}>
          <ColumnBlock cols={[
            { head: "Strengths", color: P.positive, items: scorecard.strengths || [] },
            { head: "Pain points", color: P.negative, items: scorecard.painPoints || [] },
          ]} />
        </View>
      ) : null}

      {/* Detractor verbatims */}
      {drivers.length > 0 && (
        <View wrap={false} style={s.section}>
          <Text style={s.label}>Drivers of Detraction</Text>
          {drivers.map((qt, i) => (
            <View key={i} style={s.quoteBlock}>
              <Text style={s.quoteText}>"{qt}"</Text>
            </View>
          ))}
        </View>
      )}

      <View style={s.pageFooter} fixed>
        <Text style={s.pageFooterText}>Prepared by Rik.ai  ·  Confidential</Text>
        <Text style={s.pageFooterText} render={({ pageNumber, totalPages }) =>
          `Page ${pageNumber} of ${totalPages}`
        } />
      </View>
    </Page>
  );
}

// Single content "page": all sections flow here and react-pdf auto-paginates only
// when the content overflows, so there are no forced per-section page breaks (and
// no large blank gaps). The header/footer are `fixed`, so they repeat on every
// auto-generated page; the Page padding reserves space for them.
function Body({ surveyTitle, children }) {
  return (
    <Page size="A4" style={s.page}>
      <View style={s.pageHeader} fixed>
        <Text style={s.pageHeaderBrand}>Rik.ai<Text style={s.pageHeaderBrandSub}>  ·  Confidential</Text></Text>
        <Text style={s.pageHeaderTitle}>{surveyTitle}</Text>
      </View>
      {children}
      <View style={s.pageFooter} fixed>
        <Text style={s.pageFooterText}>Prepared by Rik.ai  ·  Confidential</Text>
        <Text style={s.pageFooterText} render={({ pageNumber, totalPages }) =>
          `Page ${pageNumber} of ${totalPages}`
        } />
      </View>
    </Page>
  );
}

// ── Generic section renderer ──────────────────────────────────────────────────
function GenericSection({ sectionKey, value, surveyTitle }) {
  const title = camelToTitle(sectionKey);

  const renderValue = (val) => {
    if (val === null || val === undefined) return null;
    if (typeof val === "string" || typeof val === "number") {
      return <Text style={s.paragraph}>{String(val)}</Text>;
    }
    if (Array.isArray(val)) {
      if (val.length === 0) return null;
      if (typeof val[0] === "string") {
        return val.map((item, i) => (
          <View key={i} style={s.bulletRow}>
            <Text style={s.bullet}>•</Text>
            <Text style={s.bulletText}>{item}</Text>
          </View>
        ));
      }
      return val.map((item, i) => (
        <View key={i} style={s.genericCard}>
          {Object.entries(item).map(([k, v]) => (
            <View key={k}>
              <Text style={s.genericKey}>{camelToTitle(k)}</Text>
              <Text style={s.genericVal}>{typeof v === "object" ? JSON.stringify(v) : String(v ?? "")}</Text>
            </View>
          ))}
        </View>
      ));
    }
    if (typeof val === "object") {
      return Object.entries(val).map(([k, v]) => (
        <View key={k} style={{ marginBottom: 6 }}>
          <Text style={s.genericKey}>{camelToTitle(k)}</Text>
          <Text style={s.genericVal}>{typeof v === "object" ? JSON.stringify(v) : String(v ?? "")}</Text>
        </View>
      ));
    }
    return <Text style={s.paragraph}>{String(val)}</Text>;
  };

  return (
    <View wrap={false} style={s.section}>
      <SectionTitle>{title}</SectionTitle>
      {renderValue(value)}
    </View>
  );
}

// ── Main PDF Document ─────────────────────────────────────────────────────────
export function ReportDocument({ content, reportMeta, surveyTitle, companyName }) {
  if (!content) return null;

  const title = surveyTitle || content.surveyTitle || "Research Report";
  const em = content.engagementMetrics || {};

  // Collect unknown (custom) keys — skip ones with no real content (e.g. an
  // LLM-emitted empty "executiveSummary2") so they don't render a blank section.
  const isEmptyVal = (v) => {
    if (v === null || v === undefined) return true;
    if (typeof v === "string") return v.trim() === "";
    if (Array.isArray(v)) return v.length === 0;
    if (typeof v === "object") return Object.keys(v).length === 0;
    return false;
  };
  const customEntries = Object.entries(content).filter(
    ([k, v]) => !KNOWN_KEYS.has(k) && !isEmptyVal(v)
  );

  return (
    <Document title={title} author="Rik.ai" subject="Aggregate Research Report">

      {/* ── Cover Page ── */}
      <Page size="A4" style={s.coverPage}>
        <GradientBar id="gradTop" style={s.barTop} />
        <View style={s.coverInner}>
          <View>
            <View style={s.coverBrandRow}>
              <Image src={LOGO_SRC} style={s.coverLogoImg} />
              <Text style={s.coverWordmark}>Rik.ai</Text>
            </View>
            <Text style={s.coverTagline}>The new verse of market intelligence</Text>
          </View>

          <View>
            <Text style={s.coverEyebrow}>Aggregate Research Report</Text>
            <View style={s.coverEyebrowRule} />
            <Text style={s.coverTitle}>{title}</Text>
            {companyName && (
              <Text style={s.coverMetaLine}>Prepared for <Text style={s.coverMetaStrong}>{companyName}</Text></Text>
            )}
            {reportMeta?.generatedAt && (
              <Text style={s.coverMetaLine}>Generated <Text style={s.coverMetaStrong}>
                {new Date(reportMeta.generatedAt).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
              </Text></Text>
            )}
            {reportMeta?.version && (
              <Text style={s.coverMetaLine}>Version <Text style={s.coverMetaStrong}>v{reportMeta.version}</Text></Text>
            )}
          </View>

          <View style={s.coverFooter}>
            <Text style={s.coverFootText}>Confidential — For authorised recipients only</Text>
            <Text style={s.coverFootText}>© Rik.ai {new Date().getFullYear()}</Text>
          </View>
        </View>
        <GradientBar id="gradBot" style={s.barBottom} />
      </Page>

      <Body surveyTitle={title}>

      {/* ── Executive Summary ── */}
      {content.executiveSummary && (
        <View wrap={false} style={s.section}>
          <SectionTitle>Executive Summary</SectionTitle>
          <Text style={s.paragraph}>{content.executiveSummary}</Text>
        </View>
      )}

      </Body>

      {/* ── Methodology Scorecard (Phase 3) — omitted for qual_only studies ── */}
      {content.scorecard ? <Scorecard scorecard={content.scorecard} surveyTitle={title} /> : null}

      <Body surveyTitle={title}>

      {/* ── Action Summary (Do Now / Do Next / Protect) ── */}
      {content.actionSummary && (content.actionSummary.doNow?.length || content.actionSummary.doNext?.length || content.actionSummary.protect?.length) ? (
        <View wrap={false} style={s.section}>
          <SectionTitle>Executive Action Summary</SectionTitle>
          <ColumnBlock cols={[
            { head: "Do Now",  color: P.negative, items: content.actionSummary.doNow },
            { head: "Do Next", color: P.accent,   items: content.actionSummary.doNext },
            { head: "Protect", color: P.positive, items: content.actionSummary.protect },
          ]} />
        </View>
      ) : null}

      {/* ── Engagement Metrics ── */}
      {em.avgWpm != null && (
        <View wrap={false} style={s.section}>
          <SectionTitle>Engagement Metrics</SectionTitle>
          <View style={s.metricsRow} wrap={false}>
            {em.avgWpm != null && (
              <View style={s.metricBox}>
                <Text style={s.metricVal}>{Math.round(em.avgWpm)}</Text>
                <Text style={s.metricLabel}>Avg WPM</Text>
              </View>
            )}
            {em.avgSentimentScore != null && (
              <View style={s.metricBox}>
                <Text style={s.metricVal}>{Number(em.avgSentimentScore).toFixed(2)}</Text>
                <Text style={s.metricLabel}>Avg Sentiment</Text>
              </View>
            )}
            {em.completionRate && (
              <View style={s.metricBox}>
                <Text style={s.metricVal}>{em.completionRate}</Text>
                <Text style={s.metricLabel}>Completion</Text>
              </View>
            )}
            {em.avgFacialEngagement != null && (
              <View style={s.metricBox}>
                <Text style={s.metricVal}>{Math.round(em.avgFacialEngagement)}/100</Text>
                <Text style={s.metricLabel}>Facial Engagement</Text>
              </View>
            )}
          </View>
          {em.narrative && <Text style={s.muted}>{em.narrative}</Text>}
        </View>
      )}

      {/* ── Key Themes ── */}
      {content.keyThemes?.length > 0 && (
        <View wrap={false} style={s.section}>
          <SectionTitle>Key Themes</SectionTitle>
          {content.keyThemes.map((t, i) => (
            <View key={i} style={s.card} wrap={false}>
              <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                <View style={{ flexDirection: "row", alignItems: "center", gap: 6, flex: 1 }}>
                  <View style={[s.sentDot, { backgroundColor: sentimentColor(t.sentiment) }]} />
                  <Text style={s.cardTitle}>{t.theme}</Text>
                </View>
                <View style={{ flexDirection: "row", alignItems: "center", gap: 5 }}>
                  {t.confidence && <Text style={[s.confChip, { color: confColor(t.confidence), borderColor: confColor(t.confidence) }]}>{t.confidence} conf</Text>}
                  {t.frequency && <Text style={s.chip}>{t.frequency}</Text>}
                </View>
              </View>
              <Text style={s.cardSubtext}>{t.description}</Text>
            </View>
          ))}
        </View>
      )}

      {/* ── Drivers & Barriers ── */}
      {content.driversAndBarriers && (content.driversAndBarriers.drivers?.length || content.driversAndBarriers.barriers?.length) ? (
        <View wrap={false} style={s.section}>
          <SectionTitle>Drivers & Barriers</SectionTitle>
          <ColumnBlock cols={[
            { head: "Drivers",  color: P.positive, items: content.driversAndBarriers.drivers },
            { head: "Barriers", color: P.negative, items: content.driversAndBarriers.barriers },
          ]} />
        </View>
      ) : null}

      {/* ── Question Analysis ── */}
      {content.questionAnalysis?.length > 0 && content.questionAnalysis.map((qa, i) => (
        <View key={i} wrap={false} style={s.section}>
          <SectionTitle>Q{qa.questionIndex + 1}: {qa.questionText}</SectionTitle>
          {qa.quantitativeStat && (
            <Text style={{ fontFamily: DISPLAY, fontWeight: 600, fontSize: 11, color: P.accent, marginBottom: 6 }}>
              {qa.quantitativeStat}
            </Text>
          )}
          {qa.summary && <Text style={s.paragraph}>{qa.summary}</Text>}
          {qa.sentimentBreakdown && (
            <SentimentBar
              positive={qa.sentimentBreakdown.positive}
              neutral={qa.sentimentBreakdown.neutral}
              negative={qa.sentimentBreakdown.negative}
            />
          )}
          {qa.notableQuotes?.length > 0 && (
            <>
              <Text style={s.label}>Notable Quotes</Text>
              {qa.notableQuotes.map((q, j) => (
                <View key={j} style={s.quoteBlock} wrap={false}>
                  <Text style={s.quoteText}>“{q}”</Text>
                </View>
              ))}
            </>
          )}
          {qa.insights && (
            <Text style={[s.muted, { marginTop: 6 }]}>
              <Text style={{ fontFamily: DISPLAY, fontWeight: 600, color: P.ink }}>Insight: </Text>{qa.insights}
            </Text>
          )}
        </View>
      ))}

      {/* ── Verbatim Highlights ── */}
      {content.verbatimHighlights?.length > 0 && (
        <View wrap={false} style={s.section}>
          <SectionTitle>Verbatim Highlights</SectionTitle>
          {content.verbatimHighlights.map((vh, i) => (
            <View key={i} wrap={false} style={[s.quoteBlock, { borderLeftColor: sentimentColor(vh.sentiment), marginBottom: 10 }]}>
              <Text style={s.quoteText}>“{vh.quote}”</Text>
              <Text style={s.quoteMeta}>{vh.respondent}{vh.question ? ` · ${vh.question}` : ""}</Text>
            </View>
          ))}
        </View>
      )}

      {/* ── Sentiment Analysis ── */}
      {content.sentimentAnalysis && (
        <View break style={s.section}>
          <SectionTitle>Sentiment Analysis</SectionTitle>
          <View wrap={false} style={[s.metricsRow, { marginBottom: 12 }]}>
            <View style={s.metricBox}>
              <Text style={[s.metricVal, { color: sentimentColor(content.sentimentAnalysis.overall) }]}>
                {content.sentimentAnalysis.overall || "—"}
              </Text>
              <Text style={s.metricLabel}>Overall</Text>
            </View>
            {content.sentimentAnalysis.overallScore != null && (
              <View style={s.metricBox}>
                <Text style={s.metricVal}>{Number(content.sentimentAnalysis.overallScore).toFixed(2)}</Text>
                <Text style={s.metricLabel}>Score</Text>
              </View>
            )}
          </View>
          {content.sentimentAnalysis.narrative && (
            <Text style={s.paragraph}>{content.sentimentAnalysis.narrative}</Text>
          )}
          {content.sentimentAnalysis.byQuestion?.length > 0 && (
            <>
              <Text style={s.label}>By Question</Text>
              {content.sentimentAnalysis.byQuestion.map((bq, i) => (
                <View key={i} style={s.card} wrap={false}>
                  <View style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 2 }}>
                    <Text style={s.cardSubtext}>Q{bq.questionIndex + 1}</Text>
                    <Text style={[s.cardSubtext, { fontFamily: DISPLAY, fontWeight: 600, color: sentimentColor(bq.dominantSentiment) }]}>
                      {bq.dominantSentiment}
                    </Text>
                  </View>
                  <SentimentBar positive={bq.positive} neutral={bq.neutral} negative={bq.negative} />
                </View>
              ))}
            </>
          )}
        </View>
      )}

      {/* ── Demographic Insights ── */}
      {content.demographicInsights && (
        <View wrap={false} style={s.section}>
          <SectionTitle>Demographic Insights</SectionTitle>
          <Text style={s.paragraph}>{content.demographicInsights}</Text>
        </View>
      )}

      {/* ── Recommendations ── */}
      {content.recommendations?.length > 0 && (
        <View wrap={false} style={s.section}>
          <SectionTitle>Recommendations</SectionTitle>
          {content.recommendations.map((r, i) => (
            <View key={i} style={s.recRow} wrap={false}>
              <View style={s.recLeft}>
                <Text style={s.recTitle}>{r.title}</Text>
                <Text style={s.recDesc}>{r.description}</Text>
                <View style={{ flexDirection: "row", marginTop: 4 }}>
                  {r.impact && <Text style={s.metaTag}>Impact: {r.impact}</Text>}
                  {r.effort && <Text style={s.metaTag}>Effort: {r.effort}</Text>}
                  {r.owner && <Text style={s.metaTag}>Owner: {r.owner}</Text>}
                </View>
              </View>
              <View style={{ alignItems: "flex-end", gap: 5, width: 78 }}>
                {r.priority && (
                  <Text style={[s.pill, { backgroundColor: priorityColor(r.priority) }]}>{r.priority}</Text>
                )}
                {r.timeframe && (
                  <Text style={s.pillOutline}>{r.timeframe.replace(/_/g, " ")}</Text>
                )}
              </View>
            </View>
          ))}
        </View>
      )}

      {/* ── Conclusion ── */}
      {content.conclusion && (
        <View wrap={false} style={s.section}>
          <SectionTitle>Conclusion</SectionTitle>
          <Text style={s.paragraph}>{content.conclusion}</Text>
          {content.methodologyNote && (
            <>
              <View style={s.divider} />
              <Text style={s.label}>Methodology</Text>
              <Text style={s.muted}>{content.methodologyNote}</Text>
            </>
          )}
        </View>
      )}

      {/* ── Generic / Custom sections ── */}
      {customEntries.map(([key, value]) => (
        <GenericSection key={key} sectionKey={key} value={value} surveyTitle={title} />
      ))}

      </Body>

    </Document>
  );
}

// ── Download trigger ──────────────────────────────────────────────────────────
export async function downloadReportPdf({ content, reportMeta, surveyTitle, companyName }) {
  const blob = await pdf(
    <ReportDocument
      content={content}
      reportMeta={reportMeta}
      surveyTitle={surveyTitle}
      companyName={companyName}
    />
  ).toBlob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${(surveyTitle || "report").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-v${reportMeta?.version || 1}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}
